import { useCallback, useEffect, useRef } from 'react';
import { isPollingPaused } from './pollingControl';

const UPPLADDNINGSPAUS_MS = 2000;
const STANDARD_MAX_UTGLESNING_MS = 5 * 60 * 1000;

/**
 * Är fliken dold? Läser document.visibilityState; saknas det (gamla miljöer)
 * räknas fliken som synlig.
 */
export function flikenArDold() {
  try {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  } catch {
    return false;
  }
}

/**
 * Gemensam pollningshook för skalets bakgrundsanrop (A11, 2026-10-07).
 *
 * Beteende:
 * - Kör `fn` direkt vid start och sedan var `intervalMs`.
 * - Pausar helt när fliken är dold och hämtar direkt när den blir synlig igen.
 * - Hoppar över varv medan uppladdningspausen (pollingControl) är aktiv och
 *   försöker igen efter 2 s, som tidigare.
 * - Slumpspridning ±`jitter` (default 10 %) så att alla flikar inte frågar
 *   samtidigt efter en deploy.
 * - Utglesning vid fel: om `fn` kastar eller returnerar `false` dubblas
 *   intervallet upp till `maxBackoffMs` (default 5 min). Ett lyckat varv
 *   återställer intervallet.
 *
 * `fn` får ett objekt `{ orsak }` där orsak är 'start' | 'intervall' |
 * 'synlig' | 'manuell', och ska returnera `false` (eller kasta) vid fel.
 *
 * @param {(info: {orsak: string}) => (Promise<boolean|void>|boolean|void)} fn
 * @param {object} opts
 * @param {number} opts.intervalMs
 * @param {boolean} [opts.enabled=true] Kör inte alls när false (t.ex. ej inloggad)
 * @param {boolean} [opts.pauseWhenHidden=true]
 * @param {number} [opts.jitter=0.1]
 * @param {number} [opts.maxBackoffMs=300000]
 * @returns {{ refresh: () => Promise<void> }} refresh hämtar direkt (t.ex. när klockan öppnas)
 */
export default function usePolling(fn, opts) {
  const {
    intervalMs,
    enabled = true,
    pauseWhenHidden = true,
    jitter = 0.1,
    maxBackoffMs = STANDARD_MAX_UTGLESNING_MS,
  } = opts || {};

  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timerRef = useRef(null);
  const aktivRef = useRef(false);
  const pagarRef = useRef(false);
  const aktuelltIntervallRef = useRef(intervalMs);
  const optsRef = useRef({ intervalMs, pauseWhenHidden, jitter, maxBackoffMs });
  optsRef.current = { intervalMs, pauseWhenHidden, jitter, maxBackoffMs };

  const rensaTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const planera = useCallback((ms) => {
    rensaTimer();
    if (!aktivRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      // eslint-disable-next-line no-use-before-define
      kor('intervall');
    }, ms);
  }, [rensaTimer]); // eslint-disable-line react-hooks/exhaustive-deps

  const kor = useCallback(async (orsak) => {
    if (!aktivRef.current) return;
    const o = optsRef.current;
    if (o.pauseWhenHidden && flikenArDold()) {
      // Ingen timer: nästa varv triggas av visibilitychange.
      rensaTimer();
      return;
    }
    if (isPollingPaused()) {
      planera(UPPLADDNINGSPAUS_MS);
      return;
    }
    if (pagarRef.current) return;
    pagarRef.current = true;
    let ok = true;
    try {
      const r = await fnRef.current({ orsak });
      ok = r !== false;
    } catch {
      ok = false;
    } finally {
      pagarRef.current = false;
    }
    if (!aktivRef.current) return;
    if (ok) {
      aktuelltIntervallRef.current = o.intervalMs;
    } else {
      aktuelltIntervallRef.current = Math.min(o.maxBackoffMs, aktuelltIntervallRef.current * 2);
    }
    const bas = aktuelltIntervallRef.current;
    const spridning = o.jitter > 0 ? (Math.random() * 2 - 1) * o.jitter : 0;
    planera(Math.max(250, Math.round(bas * (1 + spridning))));
  }, [planera, rensaTimer]);

  useEffect(() => {
    if (!enabled || !intervalMs) {
      aktivRef.current = false;
      rensaTimer();
      return undefined;
    }
    aktivRef.current = true;
    aktuelltIntervallRef.current = intervalMs;

    const onVisibility = () => {
      if (!aktivRef.current) return;
      if (flikenArDold()) {
        rensaTimer();
      } else {
        kor('synlig');
      }
    };
    if (pauseWhenHidden && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    kor('start');
    return () => {
      aktivRef.current = false;
      rensaTimer();
      if (pauseWhenHidden && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [enabled, intervalMs, pauseWhenHidden, kor, rensaTimer]);

  const refresh = useCallback(() => kor('manuell'), [kor]);

  return { refresh };
}
